import { useId, useState } from 'react';
import apiClient from '../../api/apiClient';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { CalendarPlus } from 'lucide-react';

const ITEM_TYPES = [
  { key: 'meeting', label: 'Meeting' },
  { key: 'audition', label: 'Audition' },
  { key: 'callback', label: 'Callback' },
  { key: 'rehearsal', label: 'Rehearsal' },
  { key: 'shoot_date', label: 'Shoot date' },
  { key: 'follow_up', label: 'Follow-up' },
  { key: 'task', label: 'Task' },
];

/**
 * PlannerQuickAddModal — minimal planner item creator used from:
 *  - FloatingMessenger / MessagesPage thread header (context.source = 'messenger')
 *  - Connections tab connected row (context.source = 'network')
 *
 * Pre-fills the title using the other user's name and links back to the
 * source via related_item_* so planner items stay traceable.
 */
export default function PlannerQuickAddModal({ open, onClose, context = {} }) {
  const fieldId = useId();
  const { source, other_user_name, other_user_id, conversation_id, application_id, casting_title, role_name } = context;
  // Title default depends on where the modal was triggered from:
  //  - messenger      → "Meeting with X"
  //  - network        → "Connect with X"
  //  - availability_hint → "Message X" (this is the late-reply nudge use case)
  let defaultTitle;
  if (other_user_name) {
    if (source === 'application') defaultTitle = `Audition: ${other_user_name} — ${casting_title || 'Casting'}${role_name ? ` (${role_name})` : ''}`;
    else if (source === 'messenger') defaultTitle = `Meeting with ${other_user_name}`;
    else if (source === 'availability_hint') defaultTitle = `Message ${other_user_name}`;
    else defaultTitle = `Connect with ${other_user_name}`;
  } else {
    defaultTitle = 'New meeting';
  }

  const [title, setTitle] = useState(defaultTitle);
  // For availability_hint we default to a follow-up planner item — the
  // user already had the intent to "message them later", which maps
  // directly onto the existing follow_up item type. Messenger / network
  // still default to "meeting" as before.
  const [itemType, setItemType] = useState(source === 'application' ? 'audition' : source === 'availability_hint' ? 'follow_up' : 'meeting');
  const today = new Date().toISOString().slice(0, 10);
  // For availability_hint default the reminder a week out — this is the
  // headline copy in MessageAvailabilityHint ("Set a reminder to message
  // them next week"). For everything else keep the existing "today" default.
  const nextWeek = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0, 10);
  })();
  const [date, setDate] = useState(source === 'availability_hint' ? nextWeek : today);
  const [startTime, setStartTime] = useState(source === 'availability_hint' ? '09:00' : '');
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  // Reminder: 'none' | '1h' | '1d' | '1w' | 'custom'. The backend `core/
  // planner_reminders.py` scheduler reads `reminder_offset` and emits the
  // in-app notification + email when due. We expose this as a tiny pill
  // bar so the user doesn't have to leave the modal. When 'custom' is
  // selected the user picks an explicit local date+time which we convert
  // to a UTC ISO string and send as `reminder_offset` — the backend's
  // _compute_reminder_at parses ISO datetimes as absolute timestamps.
  // Default reminder timing also varies with source:
  //  - availability_hint → "1d" (remind the day before, since the
  //    planner item itself is a week out and easy to forget)
  //  - everything else   → "none" (preserve existing behaviour)
  const [reminder, setReminder] = useState(source === 'availability_hint' ? '1d' : 'none');
  const [customReminderDate, setCustomReminderDate] = useState('');
  const [customReminderTime, setCustomReminderTime] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!title.trim() || !date) {
      toast.error('Add a title and a date');
      return;
    }
    // Resolve the reminder_offset payload.
    //   • 'none'   → send null, reminder_enabled=false
    //   • '1h'/'1d'/'1w' → relative string (existing behaviour)
    //   • 'custom' → require date+time, build a UTC ISO datetime string
    //               so the backend treats it as an absolute reminder.
    let reminderOffset = null;
    if (reminder !== 'none') {
      if (reminder === 'custom') {
        if (!customReminderDate || !customReminderTime) {
          toast.error('Pick a reminder date and time');
          return;
        }
        // `new Date('YYYY-MM-DDTHH:MM')` is interpreted in the user's
        // local timezone — exactly what we want, because the user is
        // picking a wall-clock moment they want to be reminded at.
        // Convert to a UTC ISO string before sending.
        const local = new Date(`${customReminderDate}T${customReminderTime}`);
        if (Number.isNaN(local.getTime())) {
          toast.error('That reminder date/time is invalid');
          return;
        }
        reminderOffset = local.toISOString();
      } else {
        reminderOffset = reminder;
      }
    }
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        description: notes.trim(),
        item_type: itemType,
        date,
        // `start_time` is HH:MM or null — only set `all_day: true` when the
        // user did not pick a time. Sending `all_day: true` with a time
        // value passed enum validation server-side, but the saved item
        // looks weird in the calendar. Mirrors the main Planner editor.
        start_time: startTime || null,
        all_day: !startTime,
        location: location.trim(),
        related_item_id: application_id || other_user_id || conversation_id || null,
        related_item_type: source === 'application' ? 'application' : source === 'messenger' ? 'conversation' : (source === 'network' ? 'connection' : (source === 'availability_hint' ? 'connection' : null)),
        related_item_title: other_user_name || null,
        priority: 'medium',
        status: 'scheduled',
        colour: '#002FA7',
        notes: source === 'messenger'
          ? `From messenger chat`
          : source === 'network'
            ? `From Network`
            : source === 'availability_hint'
              ? `From availability hint — recipient was limited / not available when added`
              : '',
        // Reminder timing (existing backend field `reminder_offset` is a
        // free string the scheduler reads — see core/planner_reminders.py
        // and routers/planner.py::_compute_reminder_at).
        reminder_enabled: reminder !== 'none',
        reminder_offset: reminderOffset,
      };
      await apiClient.post('/api/planner/items', payload);
      toast.success('Added to your planner');
      // Notify any open Planner panels to refresh.
      try { window.dispatchEvent(new CustomEvent('planner-items-changed')); } catch {/* noop */}
      onClose?.();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('PlannerQuickAdd failed:', err?.response?.status, err?.response?.data);
      toast.error(err?.response?.data?.detail || 'Could not add to planner');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-h-[90dvh] max-w-md overflow-y-auto" data-testid="planner-quick-add-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarPlus className="h-5 w-5 text-[#002FA7]" /> Add to Planner
          </DialogTitle>
          <DialogDescription className="text-theme-muted">
            {source === 'application' ? 'Private Planner entry for this applicant. Your notes stay private. No applicant invitation is sent.' : 'Schedule a meeting, audition, call, or other activity — it will appear in your planner and, where relevant, link back to this conversation or connection.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor={`${fieldId}-title`} className="text-xs text-theme-muted">Title</Label>
            <Input id={`${fieldId}-title`} value={title} onChange={(e) => setTitle(e.target.value)} data-testid="planner-quick-title" />
          </div>
          <div>
            <Label className="text-xs text-theme-muted">Type</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {ITEM_TYPES.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setItemType(t.key)}
                  className={`min-h-11 text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    itemType === t.key
                      ? 'bg-[#002FA7] border-[#002FA7] text-white'
                      : 'border-theme text-theme-text hover:bg-theme-hover'
                  }`}
                  data-testid={`planner-quick-type-${t.key}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor={`${fieldId}-date`} className="text-xs text-theme-muted">Date</Label>
              <Input id={`${fieldId}-date`} type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="planner-quick-date" />
            </div>
            <div>
              <Label htmlFor={`${fieldId}-time`} className="text-xs text-theme-muted">{source === 'application' ? 'Time (UTC, optional)' : 'Time (optional)'}</Label>
              <Input id={`${fieldId}-time`} type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} data-testid="planner-quick-time" />
            </div>
          </div>
          <div>
            <Label htmlFor={`${fieldId}-location`} className="text-xs text-theme-muted">Location (optional)</Label>
            <Input id={`${fieldId}-location`} value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Zoom link, studio address" data-testid="planner-quick-location" />
          </div>
          <div>
            <Label htmlFor={`${fieldId}-notes`} className="text-xs text-theme-muted">Notes (optional)</Label>
            <Textarea id={`${fieldId}-notes`} rows={3} maxLength={10000} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="planner-quick-notes" />
          </div>
          <div>
            <Label className="text-xs text-theme-muted mb-1.5 block">Remind me</Label>
            <div className="flex flex-wrap gap-1.5">
              {[
                { key: 'none',   label: 'No reminder' },
                { key: '1h',     label: '1 hour before' },
                { key: '1d',     label: '1 day before' },
                { key: '1w',     label: '1 week before' },
                { key: 'custom', label: 'Custom date & time' },
              ].map((r) => (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => setReminder(r.key)}
                  className={`min-h-11 text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    reminder === r.key
                      ? 'bg-[#002FA7] border-[#002FA7] text-white'
                      : 'border-theme text-theme-text hover:bg-theme-hover'
                  }`}
                  data-testid={`planner-reminder-${r.key}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            {reminder === 'custom' && (
              <div className="grid grid-cols-2 gap-2 mt-2" data-testid="planner-reminder-custom-fields">
                <div>
                  <Label className="text-xs text-theme-muted">Reminder date</Label>
                  <Input
                    type="date"
                    value={customReminderDate}
                    onChange={(e) => setCustomReminderDate(e.target.value)}
                    data-testid="planner-reminder-custom-date"
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs text-theme-muted">Reminder time</Label>
                  <Input
                    type="time"
                    value={customReminderTime}
                    onChange={(e) => setCustomReminderTime(e.target.value)}
                    data-testid="planner-reminder-custom-time"
                    required
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose} data-testid="planner-quick-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#002FA7] hover:bg-[#0055FF] text-white" data-testid="planner-quick-save">
            {saving ? 'Saving…' : 'Add to planner'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
